import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { NetworkService, NetworkStatus } from '@shared/services';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-network-status-indicator',
  imports: [CommonModule, NzIconModule, NzToolTipModule],
  templateUrl: './network-status-indicator.component.html',
  styleUrl: './network-status-indicator.component.scss',
})
export class NetworkStatusIndicatorComponent implements OnDestroy {
  readonly enableNetworkStatusIndicator =
    environment.enableNetworkStatusIndicator;
  readonly _COMPONENT_NAME = 'NetworkStatusIndicatorComponent';
  private readonly _destroy$ = new Subject<void>();
  private _previousOnlineStatus: boolean | null = null;

  constructor(
    private readonly _networkService: NetworkService,
    private readonly _notificationService: NzNotificationService
  ) {
    this._setupNetworkStatusMonitoring();
  }

  get isOnline() {
    return this._networkService.isOnline();
  }

  get isUnstable() {
    return this._networkService.currentStatus.isUnstable || false;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Get icon type based on network status
   */
  getIconType(): string {
    if (!this.isOnline) {
      return 'disconnect';
    }
    if (this.isUnstable) {
      return 'wifi';
    }
    return 'wifi';
  }

  /**
   * Get status text
   */
  getStatusText(): string {
    if (!this.isOnline) {
      return 'Offline';
    }
    if (this.isUnstable) {
      return 'Unstable';
    }
    return 'Online';
  }

  /**
   * Get tooltip text
   */
  getTooltipText(): string {
    if (!this.isOnline) {
      return 'No internet connection. Please check your network settings.';
    }
    if (this.isUnstable) {
      return 'Unstable connection. Some features may not work properly.';
    }
    return this._networkService.getConnectionQuality();
  }

  /**
   * Setup network status monitoring and notifications
   */
  private _setupNetworkStatusMonitoring(): void {
    this._networkService.networkStatus$
      .pipe(takeUntil(this._destroy$))
      .subscribe((status: NetworkStatus) => {
        this._handleNetworkStatusChange(status);
      });
  }

  /**
   * Handle network status changes
   */
  private _handleNetworkStatusChange(status: NetworkStatus): void {
    // Only show notifications if there's an actual state change
    if (
      this._previousOnlineStatus !== null &&
      this._previousOnlineStatus !== status.isOnline
    ) {
      if (!status.isOnline) {
        this._showOfflineNotification();
      } else {
        this._showOnlineNotification();
      }
    }

    // Handle unstable network notifications
    if (status.isOnline && status.isUnstable) {
      this._showUnstableNotification();
    } else if (status.isOnline && !status.isUnstable) {
      this._hideUnstableNotification();
    }

    // Update the previous status
    this._previousOnlineStatus = status.isOnline;
  }

  /**
   * Show offline notification
   */
  private _showOfflineNotification(): void {
    this._notificationService.error(
      'No Internet Connection',
      'You are currently offline. Some features may not be available.',
      {
        nzDuration: 0,
        nzKey: 'network-offline',
        nzPlacement: 'top',
      }
    );
  }

  /**
   * Show online notification
   */
  private _showOnlineNotification(): void {
    // Hide the offline notification first
    this._notificationService.remove('network-offline');

    // Show the online notification
    this._notificationService.success(
      'Connection Restored',
      'You are back online. All features are now available.',
      {
        nzDuration: 3000,
        nzKey: 'network-online',
        nzPlacement: 'top',
      }
    );
  }

  /**
   * Show unstable network notification
   */
  private _showUnstableNotification(): void {
    this._notificationService.warning(
      'Unstable Connection',
      'Your internet connection is unstable. Some features may not work properly.',
      {
        nzDuration: 0,
        nzKey: 'network-unstable',
        nzPlacement: 'top',
      }
    );
  }

  /**
   * Hide unstable network notification
   */
  private _hideUnstableNotification(): void {
    this._notificationService.remove('network-unstable');
  }
}
